import { Badge, Group, Paper, Stack, Text, ThemeIcon, Timeline } from "@mantine/core";
import { IconAlertTriangle, IconCheck, IconInfoCircle } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { formatDate } from "../../lib/format";
import { axioTokens } from "../../theme";
import type { TimelineEntry } from "../../types/api";
import { TimelineFilters } from "./TimelineFilters";

function timelineActorLine(actor: TimelineEntry["actor"]): string | null {
  if (!actor) return null;
  const parts: string[] = [];
  if (actor.label) parts.push(actor.label);
  if (actor.name && actor.name !== actor.label) parts.push(actor.name);
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function ProfileTimeline({ entries }: { entries: TimelineEntry[] }) {
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(
    () => entries.filter((item) => filter === "all" || item.category === filter),
    [entries, filter],
  );

  return (
    <Paper withBorder p="md">
      <Stack>
        <TimelineFilters value={filter} onChange={setFilter} total={filtered.length} />
        {filtered.length === 0 ? (
          <Text c="dimmed" ta="center" py="lg">
            No events in this category yet.
          </Text>
        ) : null}
        <Timeline active={axioTokens.number.zero} bulletSize={axioTokens.unit[20]} lineWidth={axioTokens.strokeWidth.strong}>
          {filtered.map((item) => {
            const actorLine = timelineActorLine(item.actor);
            return (
            <Timeline.Item
              key={item.id}
              bullet={
                <ThemeIcon
                  size="var(--axio-unit-18)"

                  variant="filled"
                  color={
                    item.severity === "success"
                      ? "green"
                      : item.severity === "warning" || item.severity === "critical"
                        ? "orange"
                        : "violet"
                  }
                >
                  {item.severity === "success" ? (
                    <IconCheck size="var(--axio-unit-12)" />
                  ) : item.severity === "warning" || item.severity === "critical" ? (
                    <IconAlertTriangle size="var(--axio-unit-12)" />
                  ) : (
                    <IconInfoCircle size="var(--axio-unit-12)" />
                  )}
                </ThemeIcon>
              }
              title={
                <Stack gap="var(--axio-unit-4)">
                  <Group gap="var(--axio-unit-8)">
                    <Text fw="var(--axio-font-weight-semibold)">{item.title}</Text>
                    <Badge size="xs" variant="light">
                      {item.category}
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {formatDate(item.occurred_at)}
                  </Text>
                </Stack>
              }
            >
              <Stack gap="var(--axio-unit-6)">
                <Badge variant="light" w="fit-content">
                  {item.action}
                </Badge>
                {actorLine ? <Text size="sm">{actorLine}</Text> : null}
                {Object.keys(item.metadata ?? {}).length > 0 ? (
                  <Stack gap="var(--axio-unit-2)">
                    {Object.entries(item.metadata).map(([key, value]) => (
                      <Text key={key} size="sm" c="dimmed">
                        {key}: {String(value)}
                      </Text>
                    ))}
                  </Stack>
                ) : null}
              </Stack>
            </Timeline.Item>
            );
          })}
        </Timeline>
      </Stack>
    </Paper>
  );
}
